import { isArray } from 'lodash';

import { InitialMockData } from './initialMockData';

const DB_NAME = 'AdManagerDemoEnvDB';

type UseDevEnvironmentData = {
  setInitialData: () => void;
  addNewItem: (storeName: string, item: never) => Promise<void>;
  getItem: (storeName: string, item: never) => Promise<never>;
  getAllItems: (storeName: string) => Promise<never[]>;
  updateItem: (storeName: string, item: { id: string; value: boolean } | never) => Promise<void>;
};

let openDBRequest: IDBOpenDBRequest | null = null;

const useDevEnvironmentData = (): UseDevEnvironmentData => {
  const getLastVersion = async (): Promise<number> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME);

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        db.close();
        resolve(db.version);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });

  const addNewItem = async (storeName: string, item: never): Promise<void> => {
    let currentVersion;

    try {
      currentVersion = await getLastVersion();
    } catch (error) {
      console.error(error);
      return;
    }

    const newVersion = currentVersion + 1;
    const newVersionRequest = indexedDB.open(DB_NAME, newVersion);

    newVersionRequest.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
      }
    };

    newVersionRequest.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const addRequest = store.add(item);

      addRequest.onerror = (event) => {
        console.error((event.target as IDBRequest).error);
      };
    };
  };

  const getItem = async (storeName: string, id: never): Promise<never> =>
    new Promise((resolve, reject) => {
      openDBRequest = indexedDB.open(DB_NAME);

      openDBRequest.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(storeName)) {
          return;
        }

        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const getRequest = store.get(id);

        getRequest.onsuccess = (event) => {
          resolve((event.target as IDBRequest).result);
          openDBRequest = null;
        };

        getRequest.onerror = (event) => {
          console.error((event.target as IDBOpenDBRequest).error);
          reject();
        };
      };

      openDBRequest.onerror = (event) => {
        console.error((event.target as IDBOpenDBRequest).error);
      };
    });

  const getAllItems = async (storeName: string): Promise<never[]> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME);

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(storeName)) {
          return;
        }

        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const getRequest = store.getAll();

        getRequest.onsuccess = (event) => {
          resolve((event.target as IDBRequest).result as never[]);
        };

        getRequest.onerror = (event) => {
          console.error((event.target as IDBOpenDBRequest).error);
          reject();
        };
      };

      request.onerror = (event) => {
        console.error((event.target as IDBOpenDBRequest).error);
      };
    });

  const updateItem = async (storeName: string, newValue: { id: string; value: boolean } | never): Promise<void> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME);

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const putRequest = store.put(newValue);

        putRequest.onsuccess = (event) => {
          resolve();
        };

        putRequest.onerror = (event) => {
          console.error((event.target as IDBOpenDBRequest).error);
          reject();
        };
      };

      request.onerror = (event) => {
        console.error((event.target as IDBOpenDBRequest).error);
        reject();
      };
    });

  const setInitialData = async () => {
    const currentVersion = await getLastVersion();

    if (currentVersion === 1) {
      const newVersionRequest = indexedDB.open(DB_NAME, 2);

      newVersionRequest.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        for (const storeName in InitialMockData) {
          db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
        }
      };

      newVersionRequest.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        for (const [storeName, value] of Object.entries(InitialMockData)) {
          const transaction = db.transaction(storeName, 'readwrite');
          const store = transaction.objectStore(storeName);

          (isArray(value) ? value : [value]).forEach((item) => {
            store.add(item);
          });

          transaction.onerror = (event) => {
            console.error((event.target as IDBOpenDBRequest).error);
          };
        }
      };
    }
  };

  return { setInitialData, addNewItem, getItem, getAllItems, updateItem };
};

export default useDevEnvironmentData;
